import BackButton from '@/components/ui/BackButton'
import { type APIUserDetail, useUserByEmailQuery, useUserOrdersQuery } from '@/services/users'
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline'
import { Alert, Box, Snackbar, Typography } from '@mui/material'
import { useFlag } from '@unleash/proxy-client-react'
import { useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import PreviousOrdersList from '../../shared/PreviousOrdersList'
import UserDetails from './sections/user-details'
import UserPatientsSection from './sections/user-patients-section'
import UserSubscriptionsSection from './sections/user-subscriptions-section'
import UserWhatsAppConsent from './sections/user-whatsapp-consent'
import { useSubscriptionsByUserHashQuery } from '@/services/subscriptions'
import { InlineLoadingSpinner } from '@/components/common/LoadingSpinner'
import { ErrorBoundary } from '@/components/common/ErrorBoundary'

function UserDetailContent({ email }: { email: string }) {
  const [showSuccess, setShowSuccess] = useState(false)

  const { data: user, isLoading, isFetching } = useUserByEmailQuery(email)
  const isUserLoading = isLoading || isFetching
  const isWhatsAppConsentEnabled = useFlag('support-whatsapp-consent')

  const handleSaveUser = (updatedData: Partial<APIUserDetail>) => {
    console.log('Saving user:', updatedData)
    setShowSuccess(true)
  }
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 4, pb: 4 }}>
      {/* Success Message */}
      <Snackbar
        open={showSuccess}
        autoHideDuration={4000}
        onClose={() => setShowSuccess(false)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setShowSuccess(false)}
          severity='success'
          sx={{ width: '100%' }}
          icon={<CheckCircleOutlineIcon sx={{ color: '#2E7D32' }} />}
        >
          User details updated
        </Alert>
      </Snackbar>

      {/* Back Link */}
      <BackButton fallbackPath='/users' defaultLabel='Back to users list' />

      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center' }}>
        <Typography variant='pageTitle' sx={{ fontSize: '24px' }}>
          {isUserLoading ? 'Loading user details...' : `${user?.firstName} ${user?.lastName}`}
        </Typography>
      </Box>

      {/* Content */}
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
        {/* User Details Section */}
        {isUserLoading ? (
          <InlineLoadingSpinner text='Loading user details...' />
        ) : (
          <UserDetails user={user!} onSave={handleSaveUser} />
        )}

        {/* WhatsApp Consent Section */}
        {isWhatsAppConsentEnabled && !isUserLoading && user && <UserWhatsAppConsent customerHash={user.hash} />}

        {/* Patients Section */}
        {isUserLoading ? (
          <InlineLoadingSpinner text='Loading user patients...' />
        ) : (
          <UserPatientsSection patients={user!.patients} />
        )}

        {/* Subscriptions Section */}
        <UserSubscriptionsDisplay user={user} />

        {/* Orders Section */}
        <UserOrdersDisplay user={user} />
      </Box>
    </Box>
  )
}

function UserSubscriptionsDisplay({ user }: { user?: APIUserDetail | null }) {
  const navigate = useNavigate()
  const { data: subscriptions, isLoading, isFetching } = useSubscriptionsByUserHashQuery(user?.hash)

  return (
    <UserSubscriptionsSection
      subscriptions={subscriptions ?? []}
      isLoading={!user || isLoading || isFetching}
      onItemClick={hash =>
        navigate(`/subscriptions/${hash}`, {
          state: {
            backLabel: 'Back to User',
            fallbackPath: user?.email ? `/users/${encodeURIComponent(user.email)}` : '/users'
          }
        })
      }
    />
  )
}

function UserOrdersDisplay({ user }: { user?: APIUserDetail | null }) {
  const navigate = useNavigate()
  const {
    data: orders,
    isLoading,
    isFetching
  } = useUserOrdersQuery(user?.email ?? '', {
    enabled: !!user
  })

  return (
    <PreviousOrdersList
      orders={orders ?? []}
      title='Orders'
      onItemClick={hash =>
        navigate(`/orders/${hash}`, {
          state: {
            backLabel: 'Back to User',
            fallbackPath: user?.email ? `/users/${encodeURIComponent(user.email)}` : '/users'
          }
        })
      }
      isLoading={isLoading || isFetching}
    />
  )
}

function UserNotFoundFallback({ email }: { email: string }) {
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 4, p: 4 }}>
      <BackButton fallbackPath='/users' defaultLabel='Back to users list' />

      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 2,
          py: 8
        }}
      >
        <Typography variant='h5' color='text.secondary'>
          User not found
        </Typography>
        <Typography variant='body2' color='text.secondary'>
          No user exists with the email address: {email}
        </Typography>
      </Box>
    </Box>
  )
}

export default function UserDetailPage() {
  const { email } = useParams<{ email: string }>()
  if (!email) {
    return (
      <Box sx={{ p: 4 }}>
        <Typography>Invalid user email</Typography>
      </Box>
    )
  }

  const decodedEmail = decodeURIComponent(email)

  return (
    <ErrorBoundary fallback={<UserNotFoundFallback email={decodedEmail} />}>
      <UserDetailContent email={decodedEmail} />
    </ErrorBoundary>
  )
}
